// ============================================================================
// Authenticated Pig Dailys active-group bridge
// ============================================================================
//
// /webform-pigs is login-required, while PigDailysWebform deliberately remains
// an auth-free presenter under src/webforms. This narrow app-side seam owns the
// authenticated active_groups query and injects only the raw option list the
// presenter already validates before rendering.
//
// Do not move this into src/webforms. A webform presenter must not select an
// authenticated Query scope or acquire a webform_config ownership edge. The
// outer guard unmounts the query owner before a logged-out route could create a
// public-scope request; the owner itself additionally waits for resolved
// profile/session state.
//
// IT NOW OWNS THE FORM'S FIELD CONFIGURATION TOO, AND THAT ARRIVED DIFFERENTLY.
//
// `activePigGroups` replaced a read App used to perform in a sidecar effect.
// `webformsConfig` replaces something the presenter took straight off
// WebformsConfigContext — the ppp-webforms-v1 document App's global boot
// hydrated. Reading it through webform.pigDailys.config, in this surface's own
// authenticated scope, is what lets `webform` retire from the frozen app_store
// consumer list in tests/fixtures/former-eager-dependencies.json.
//
// The corollary is the one that matters: PigDailysWebform must never call
// useWebformsConfig() again, and this value must never travel as a WebformsEntry
// prop. Both are channels tests/static/former_eager_evidence_static.test.js
// scores as consumption, so either would make that retirement a false claim.
// ============================================================================
import React from 'react';

import {useAuth} from '../../contexts/AuthContext.jsx';
import {DEFAULT_WEBFORMS_CONFIG} from '../../lib/defaults.js';
import PigDailysWebform from '../../webforms/PigDailysWebform.tsx';
import type {SessionSubmitter} from '../../webforms/webformsRoutes.js';
import {usePigDailysActiveGroups} from './pigDailysActiveGroupsData.js';
import {RESOURCE} from './routeDataCatalog.js';
import type {WebformPigDailysConfigData} from './routeDataCatalog.js';
import {useRouteResource} from './useRouteResource.js';

type PigDailysPresenterProps = {
  activePigGroups?: readonly unknown[];
  webformsConfig?: unknown;
};

type PigDailysAuthenticatedBridgeProps = {
  children: React.ReactElement<PigDailysPresenterProps>;
};

const PIG_DAILYS_CONFIG_PARAMS = Object.freeze({});

function AuthenticatedPigDailysGroups({children}: PigDailysAuthenticatedBridgeProps) {
  // The historical App sidecar rendered an empty selector while this request
  // was pending or failed. The data owner preserves that projection as `[]`,
  // so no loading/error UI is injected into the existing direct form.
  const {groups} = usePigDailysActiveGroups();
  // Same posture for the config document, and its pending value is not a new
  // state the app has never had: DEFAULT_WEBFORMS_CONFIG is literally what
  // WebformsConfigContext holds until App's boot replaces it, so a pending read
  // renders the form exactly as an unhydrated context did. The resource resolves
  // a failed or absent row to the same constant, which is why there is one
  // fallback here rather than a loading branch and an error branch — a field
  // form that must work offline does not gain a full-page error screen for an
  // optional-field configuration.
  const {data} = useRouteResource(RESOURCE.WEBFORM_PIG_DAILYS_CONFIG, PIG_DAILYS_CONFIG_PARAMS);
  const webformsConfig = (data as WebformPigDailysConfigData | null)?.webformsConfig ?? DEFAULT_WEBFORMS_CONFIG;
  return React.cloneElement(children, {activePigGroups: groups, webformsConfig});
}

export default function PigDailysAuthenticatedBridge({children}: PigDailysAuthenticatedBridgeProps) {
  const {authState} = useAuth();

  // App's LoginScreen gate normally makes this branch unreachable. Keep the
  // local fail-closed guard so a future pre-gate mount cannot turn this
  // authenticated config lookup into a public-scope request.
  if (authState === null || authState === false) return null;

  return <AuthenticatedPigDailysGroups>{children}</AuthenticatedPigDailysGroups>;
}

// ============================================================================
// The mount for the RELEASED `/webform-pigs` surface
// ============================================================================
//
// `renderReleasedAuthenticatedSurface` is a plain function, not a component, so
// it cannot call `useAuth` — and App, which normally supplies `sessionSubmitter`
// as a WebformsEntry prop, is not running at all on a released route. The value
// is nevertheless reconstructible: App derives it from `authState` alone
// (src/App.tsx, `const sessionSubmitter = {`), and PigDailysWebform reads only
// `sessionSubmitter?.name`, which is why `SessionSubmitter` declares only that
// member. The derivation must sit OUTSIDE src/webforms — that tree is held
// AuthContext-free by tests/static/public_webforms_boundary_static.test.js — so
// it lives here, beside the authenticated seam that already holds `authState`,
// exactly as AddFeedAuthenticatedBridge holds Add Feed's copy.
//
// It is a SECOND mount, not a replacement. WebformsEntry keeps passing App's
// own `sessionSubmitter` on the legacy path; both paths coexist and neither
// observes the other, which is what keeps the release additive.
export function PigDailysWebformMount() {
  const {authState} = useAuth();

  // Same fail-closed shape as the bridge above: an unresolved identity renders
  // nothing rather than an anonymous form under an authenticated route.
  if (authState === null || authState === false) return null;

  // Byte-for-byte App's formula for the one member the presenter reads.
  const sessionSubmitter: SessionSubmitter = {
    name: authState.name || authState.user?.email || '',
  };

  return (
    <PigDailysAuthenticatedBridge>
      <PigDailysWebform sessionSubmitter={sessionSubmitter} />
    </PigDailysAuthenticatedBridge>
  );
}
